// Phase 1 reframe (R2): replace the four invented Builder / Cabinetmaker /
// Specifier / Reseller chip groupings with the four 2026 target project
// types from the brief.
//
// Each project type maps to a list of Zoho Contact_Type picklist values that
// represent contacts likely to specify into that project class. The default
// pool serves contacts whose Contact_Type matches at least one of the
// selected project types AND is NOT in OUT_OF_SCOPE_CONTACT_TYPES.
//
// Each project-type list is marked `// brent-validate` — Brent reviews in
// PR. The mappings are best-guess from public industry conventions; some
// Zoho values may be ambiguous (e.g. "Builder" alone — applies to all four
// project types?). Erring on the side of inclusion since the alternative
// (false negatives) means missed dials, while inclusion only means a few
// noisy contacts the rep can skip past.
//
// All project-type values referenced in the lists below also appear in
// ALL_CONTACT_TYPES so a unit test can check for typos.

export type ProjectType = 'multi_residential' | 'high_rise' | 'aged_care' | 'office_fitouts';

export const PROJECT_TYPES: readonly ProjectType[] = [
  'multi_residential',
  'high_rise',
  'aged_care',
  'office_fitouts',
];

export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  multi_residential: 'Multi-residential',
  high_rise: 'High-rise apartments',
  aged_care: 'Aged care',
  office_fitouts: 'Office fit-outs',
};

// Best-guess project-type → Contact_Type mapping. Brent validates per chip.
export const PROJECT_TYPE_TARGETS: Record<ProjectType, readonly string[]> = {
  // brent-validate: residential builders + multi-unit specifiers + designers
  multi_residential: [
    'Builder',
    'Builder - Residential',
    'Builder - Modular',
    'Architect',
    'Specifier',
    'Developer',
    'Project Manager',
  ],
  // brent-validate: high-rise tower specifiers and commercial builders
  high_rise: [
    'Architect',
    'Specifier',
    'Builder - Commercial',
    'Commercial Builder',
    'Commercial Interiors',
    'Developer',
    'Project Manager',
  ],
  // brent-validate: aged-care / retirement living specifiers
  aged_care: [
    'Architect',
    'Specifier',
    'Builder - Commercial',
    'Commercial Builder',
    'Project Manager',
    'Property Maintenance',
  ],
  // brent-validate: commercial office interior fitouts
  office_fitouts: [
    'Architect',
    'Specifier',
    'Commercial Interiors',
    'Builder - Commercial',
    'Commercial Builder',
    'Project Manager',
  ],
};

// Contact types we never auto-pool — out of strategic scope per reframe.
// Reps can still reach these via search; the default pool just doesn't
// surface them.
export const OUT_OF_SCOPE_CONTACT_TYPES: readonly string[] = [
  'Cabinet',
  'Cabinetmaker',
  'Cabinetmaker Supplie', // sic — Zoho typo, kept to match real data
  'End User',
  'Locksmith',
  'Glazier',
  'Salvage Store',
  'Standard',
  'Direct',
  'Customer',
  'N/A',
  'Other',
  'Media',
];

// Full raw Contact_Type picklist from Zoho (kept identical to the prior
// ALL_COMPANY_TYPES so the "Advanced" expander in FilterPanel still works
// for power users who want a value outside the project-type chips).
export const ALL_CONTACT_TYPES: readonly string[] = [
  'Architect',
  'Builder',
  'Builder - Commercial',
  'Builder - Modular',
  'Builder - Residential',
  'Buying Group',
  'Cabinet',
  'Cabinetmaker',
  'Cabinetmaker Supplie',
  'Commercial',
  'Commercial Builder',
  'Commercial Interiors',
  'Customer',
  'Developer',
  'Direct',
  'Door Shop',
  'End User',
  'Fabricator',
  'Fire Door Manufacturer',
  'Glazier',
  'Joinery',
  'Landscaper',
  'Locksmith',
  'Media',
  'N/A',
  'Other',
  'Project Manager',
  'Property Maintenance',
  'Prospect',
  'Reseller',
  'Reseller - Commercial',
  'Retail',
  'Salvage Store',
  'Specifier',
  'Standard',
  'Supplier',
  'Trade',
  'Wholesale',
  'Window & Doors',
];

export function expandProjectType(type: ProjectType): readonly string[] {
  return PROJECT_TYPE_TARGETS[type];
}

// Given selected project-type chips + advanced raw values, returns the flat
// list of Contact_Type picklist values to send to the worker. Always
// excludes OUT_OF_SCOPE_CONTACT_TYPES (even if they appear in advanced —
// the chip says "default pool", and the OOS list is opt-in via search).
export function resolveContactTypes(input: {
  projectTypes?: readonly ProjectType[];
  advanced?: readonly string[];
}): string[] {
  const out = new Set<string>();
  for (const t of input.projectTypes ?? []) {
    for (const v of PROJECT_TYPE_TARGETS[t]) out.add(v);
  }
  for (const v of input.advanced ?? []) out.add(v);
  for (const oos of OUT_OF_SCOPE_CONTACT_TYPES) out.delete(oos);
  return [...out];
}
